import { actions as formActions } from 'react-redux-form';

import CommunicationWorkflowDuck from './CommunicationWorkflowDuck';

import { entity as emailSnippets } from '../ducks/emailSnippets';
import { actions as customerActions } from '../ducks/customer';
import { getTokens as getTokensAction } from '../actions/tokens';
import createEmail from '../actions/createEmail';
import previewEmail from '../actions/previewEmail';
import relatedContacts from '../actions/relatedContacts';
import moment from 'moment';
import { zonedTimeToUtc } from 'date-fns-tz';
import React from 'react';

export default class EmailWorkflowDuck extends CommunicationWorkflowDuck {
  constructor() {
    super();

    this.name = 'emailWorkflow';

    this.defaultModelState = {
      email: { snippet: { subject: '', reply_to: '', body: '' }, recipients: [] },
      contacts: { relation: [], contact_ids: [] },
      scheduled_at: { date: '', time: '' }
    };

    this.actions = this.actions.bind(this);
    this.getCustomer = this.getCustomer.bind(this);
    this.getRelatedContacts = this.getRelatedContacts.bind(this);
    this.getTokens = this.getTokens.bind(this);
    this.sendEmail = this.sendEmail.bind(this);
    this.sendEmailWithSearch = this.sendEmailWithSearch.bind(this);

    this.setPreviewContent = this.setPreviewContent.bind(this);
    this.setPreviewRecipientId = this.setPreviewRecipientId.bind(this);
    this.setRecipients = this.setRecipients.bind(this);
    this.setSnippet = this.setSnippet.bind(this);
    this.validateSnippet = this.validateSnippet.bind(this);
    this.setSchedule = this.setSchedule.bind(this);
    this.validateSchedule = this.validateSchedule.bind(this);

  }

  actions() {
    return Object.assign({}, super.actions(), {
      getCustomer: this.getCustomer,
      getRelatedContacts: this.getRelatedContacts,
      getTokens: this.getTokens,
      sendEmail: this.sendEmail,
      sendEmailWithSearch: this.sendEmailWithSearch,

      setPreviewRecipientId: this.setPreviewRecipientId,
      setSnippet: this.setSnippet,
      validateSnippet: this.validateSnippet,
      setSchedule: this.setSchedule

    });
  }

  getCustomer() {
    return (dispatch) => {
      return dispatch(customerActions.load())
        .then((response) => {
          dispatch(
            formActions.change(
              `${this.name}.model.email.snippet.reply_to`,
              response.customer.email_reply_to
            )
          );
        });
    }
  };

  getRelatedContacts(model, recipientFilter, schoolYearId, combineFamilyContacts) {
    return (dispatch) => {
      return dispatch(formActions.submitFields(`${this.name}.model.contacts`,
        dispatch(relatedContacts(model.relation || [], model.contact_ids, schoolYearId, combineFamilyContacts))
          .then((response) => {
            const recipients = this.createRecipientsList(response, recipientFilter);

            dispatch(formActions.change(`${this.name}.model.email.recipients`, recipients));

            return Promise.resolve(response);
          })
          // We catch and re-reject to deference the errors. Ugly.
          .catch((response) => Promise.reject(response.errors))
      )
      )
    }
  };

  getTokens() {
    return (dispatch) => {
      return dispatch(getTokensAction('email')).then((response) => {
        dispatch(formActions.change(`${this.name}.model.email.snippet.tokens`, response.tokens));
      });
    }
  };

  sendEmail(recipients, snippet, schedule) {
    return (dispatch) => {
      //noinspection JSUnusedLocalSymbols
      const { tokens, ...dupSnippet } = snippet;

      dispatch(formActions.setPending(`${this.name}.model.email`));

      return dispatch(createEmail({ recipients: recipients, snippet: dupSnippet, schedule }));
    }
  };

  sendEmailWithSearch(search, relation, snippet, schedule, filterType, filterContactIds, schoolYearId) {
    return (dispatch) => {
      // noinspection JSUnusedLocalSymbols
      const { tokens, ...dupSnippet } = snippet;

      dispatch(formActions.setPending(`${this.name}.model.email`));
      return dispatch(createEmail({
        search,
        filter_type: filterType,
        filter_contact_ids: filterContactIds,
        relation,
        schedule,
        snippet: dupSnippet,
        school_year_id: schoolYearId
      }));
    }
  };

  setPreviewRecipientId(previewContact, snippet) {
    return (dispatch) => {
      dispatch(this.setIsLoadingPreviewContent(true));
      dispatch({ type: this.types().SET_PREVIEW_CONTENT, previewContent: null });

      dispatch(previewEmail({
        recipients: [previewContact],
        snippet
      }))
        .then((response) => dispatch(this.setPreviewContent(response.snippet_preview[0])))
        .finally(() => dispatch(this.setIsLoadingPreviewContent(false)));

      return dispatch({ type: this.types().SET_ACTIVE_PREVIEW, previewRecipientId: previewContact.id });
    }
  }

  setRecipients(contactIds) {
    return (dispatch) => dispatch(
      formActions.change(`${this.name}.model.contacts.contact_ids`, contactIds)
    )
  };

  setSnippet(snippet, customer) {
    return (dispatch) => (
      dispatch(emailSnippets.actions.get(snippet.id))
        .then(({ entities }) => {
          const selectedSnippet = entities.emailSnippets[snippet.id];
          const newModel = {
            body: selectedSnippet.body,
            reply_to: selectedSnippet.reply_to || customer.email_reply_to,
            cc_emails: selectedSnippet.cc_emails,
            bcc_emails: selectedSnippet.bcc_emails,
            subject: selectedSnippet.subject,
            permissions: snippet.permissions,
            snippet_id: snippet.id
          };

          return dispatch(formActions.merge(`${this.name}.model.email.snippet`, newModel))
        })
    )
  };

  setSchedule(schedule, customer) {
    return (dispatch) => {
      return dispatch(formActions.submitFields(`${this.name}.model.scheduled_at`, this.validateSchedule(schedule, customer)
      ));
    };
  }

  validateSchedule(schedule, customer) {
    if (schedule.schedule_type === 'scheduled') {
      // creates a datetime moment can use for comparison
      const combinedDateTime = moment(schedule.date + ' ' + schedule.time, 'YYYY-MM-DD HH:mm');
      // check this datetime is valid
      if (!combinedDateTime.isValid()) {
        return Promise.reject({ date: ['or time selection is invalid.'] });
      }
      // Confirm that the current selected time is in the future
      const combinedDateTimeTz = moment(zonedTimeToUtc(combinedDateTime, customer.time_zone));
      const momentTz = moment(zonedTimeToUtc(moment(), customer.time_zone));
      const compareTime = combinedDateTimeTz.isBefore(momentTz);
      if (compareTime) {
        return Promise.reject({ time: ['must be in the future.'] });
      }
      return Promise.resolve(schedule);
    } else {
      return Promise.resolve(schedule);
    }
  }

  validateSnippet(snippet) {
    return (dispatch) => {
      //noinspection JSUnusedLocalSymbols
      const { tokens, ...dupValues } = snippet;

      dupValues.validate = true;

      return dispatch(formActions.submitFields(`${this.name}.model.email.snippet`,
        dispatch(emailSnippets.actions.validate(dupValues))
          // We catch and re-reject to deference the errors. Ugly.
          .catch((response) => {
            return Promise.reject(response.errors);
          })
      ));
    }
  };
}
